import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Form } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { WARRANTY_PERMISSION, hasWarrantyPermission } from "../common/warrantyAccess";
import { Loading, EmptyState, ErrorState } from "../common/WarrantyStates";
import WarrantyPage from "../common/WarrantyPage";
import { listAwaitingInvoices } from "../common/warrantyApi";
import { usePagedList } from "../common/usePagedList";
import { useWarrantyNav } from "../common/useWarrantyNav";
import { formatDate } from "../common/format";
import { awaitingStatusLabel, registeredViaLabel } from "../common/opsLabels";
import {
  DUE_SOON_DAYS,
  PAGE_SIZE,
  STATUS_FILTERS,
  daysLeftLabel,
  filtersFromParams,
  filtersToParams,
  isDueSoon,
  listQuery,
  statusCount,
} from "../common/awaitingInvoiceList";
import "../warranty.css";

const STATUS_CHIP_LABEL = {
  awaiting_invoice: "Awaiting invoice",
  expired: "Expired",
  all: "All",
};

const DUE_OPTIONS = [
  { value: "", label: "Any" },
  { value: "0", label: "Today or overdue" },
  { value: "2", label: "2 days" },
  { value: "7", label: "7 days" },
  { value: "14", label: "14 days" },
  { value: "30", label: "30 days" },
];

const dash = (value) => (value === null || value === undefined || value === "" ? "-" : value);

const ChipCount = ({ value }) =>
  value === null || value === undefined ? null : <span className="warranty-tab-count">{value}</span>;

const StatusBadges = ({ item }) => (
  <>
    <span className={`warranty-badge is-await-${item.status}`}>{awaitingStatusLabel(item.status)}</span>
    {item.status === "awaiting_invoice" && item.overdue && (
      <span
        className="warranty-badge is-cancelled warranty-attention-badge"
        title="The due date has passed; the daily expiry job has not run yet."
      >
        Overdue, expiry pending
      </span>
    )}
    {isDueSoon(item) && (
      <span className="warranty-badge is-warning warranty-attention-badge" title={`Due within ${DUE_SOON_DAYS} days`}>
        Due soon
      </span>
    )}
  </>
);

// Registrations whose rider chose "Upload invoice later". Read only: nothing is decided here; once
// the invoice arrives the registration is checked automatically or reaches the review queue.
// No row opens a detail screen: the review panel opens from a review queue row only, and its
// decisions do not apply to a registration still waiting for its invoice.
const AwaitingInvoices = () => {
  const { selfInfo, accessToken } = useSelector((state) => state.adminReducers);
  const allowed = hasWarrantyPermission(selfInfo, WARRANTY_PERMISSION.OPS);
  useWarrantyNav(allowed);

  // Filters live in the URL, so a reload or a shared link keeps them.
  const [params, setParams] = useSearchParams();
  const paramText = params.toString();
  const filters = useMemo(() => filtersFromParams(new URLSearchParams(paramText)), [paramText]);
  const { status, dueWithinDays, overdue, sort } = filters;
  const expired = status === "expired";

  const setFilter = (patch) => setParams(filtersToParams({ ...filters, ...patch }), { replace: true });

  const fetchPage = useCallback(
    (cursor) =>
      listAwaitingInvoices(accessToken, listQuery({ status, dueWithinDays, overdue, sort }, cursor, PAGE_SIZE)),
    [accessToken, status, dueWithinDays, overdue, sort]
  );
  const list = usePagedList(fetchPage, allowed);

  // why: the counts are of the whole market whatever the filters, and come on the first page only,
  // so the last ones seen stay on the chips while another filter loads.
  const [lastCounts, setLastCounts] = useState(null);
  const firstPageCounts = list.firstPage?.counts;
  useEffect(() => {
    if (firstPageCounts) {
      setLastCounts(firstPageCounts);
    }
  }, [firstPageCounts]);
  const counts = firstPageCounts || lastCounts;

  const pickStatus = (key) => {
    if (key === status) {
      return;
    }
    // The due and overdue filters match awaiting rows only.
    setFilter(key === "expired" ? { status: key, dueWithinDays: "", overdue: false } : { status: key });
  };

  const toggleOverdue = () =>
    setFilter(
      overdue
        ? { overdue: false }
        : { overdue: true, ...(expired ? { status: "awaiting_invoice" } : {}) }
    );

  return (
    <WarrantyPage
      title="Awaiting invoice"
      sub="Registrations whose rider chose to upload the invoice later, and those whose window ran out. Market IN. Read only."
      allowed={allowed}
      deniedTitle="You do not have access to the awaiting invoice list"
      deniedText="Ask an admin to give you the Warranty Operations permission."
      actions={
        <Button className="Addbtn" onClick={() => list.reload(false)} disabled={list.status === "loading"}>
          Reload
        </Button>
      }
    >
      <div className="warranty-window-picker warranty-status-chips" role="group" aria-label="Status">
        {STATUS_FILTERS.map((key) => (
          <button
            key={key}
            type="button"
            className={`warranty-inline-btn${status === key ? " is-selected" : ""}`}
            aria-pressed={status === key}
            onClick={() => pickStatus(key)}
          >
            {STATUS_CHIP_LABEL[key]} <ChipCount value={statusCount(counts, key)} />
          </button>
        ))}
        <button
          type="button"
          className={`warranty-inline-btn${overdue ? " is-selected" : ""}`}
          aria-pressed={overdue}
          onClick={toggleOverdue}
        >
          Overdue only <ChipCount value={Number.isInteger(counts?.overdue) ? counts.overdue : null} />
        </button>
      </div>

      <div className="formloginset warranty-list-filters">
        <Form.Group controlId="warrantyAwaitingDue">
          <Form.Label>Due within</Form.Label>
          <Form.Select
            value={dueWithinDays}
            disabled={expired}
            title={expired ? "Expired registrations have no due date left" : undefined}
            onChange={(event) => setFilter({ dueWithinDays: event.target.value })}
          >
            {DUE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
        <Form.Group controlId="warrantyAwaitingSort">
          <Form.Label>Sort</Form.Label>
          <Form.Select value={sort} onChange={(event) => setFilter({ sort: event.target.value })}>
            <option value="due_asc">Soonest due first</option>
            <option value="due_desc">Latest due first</option>
          </Form.Select>
        </Form.Group>
      </div>

      {list.status === "loading" && <Loading label="Loading registrations" />}
      {list.status === "error" && <ErrorState message={list.message} onRetry={() => list.reload()} />}
      {list.status === "ready" && list.items.length === 0 && (
        <EmptyState title="No registrations match these filters">
          <span>Try another status or clear a filter.</span>
        </EmptyState>
      )}
      {list.status === "ready" && list.items.length > 0 && (
        <>
          <div className="tableView warranty-ops-table">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">Frame number</th>
                  <th scope="col">Product</th>
                  <th scope="col">Market</th>
                  <th scope="col">Registered on</th>
                  <th scope="col">Invoice due by</th>
                  <th scope="col">Days left</th>
                  <th scope="col">Status</th>
                  <th scope="col">Registered via</th>
                  <th scope="col">Phone</th>
                </tr>
              </thead>
              <tbody>
                {list.items.map((item) => (
                  <tr key={item.registrationId}>
                    <td className="warranty-nowrap">{dash(item.frameNumber)}</td>
                    <td>{dash(item.productName)}</td>
                    <td className="warranty-nowrap">{dash(item.country)}</td>
                    <td className="warranty-nowrap">{formatDate(item.createdAt)}</td>
                    <td className="warranty-nowrap">{formatDate(item.invoiceDueBy)}</td>
                    <td className="warranty-nowrap">{daysLeftLabel(item)}</td>
                    <td>
                      <StatusBadges item={item} />
                    </td>
                    <td className="warranty-nowrap">{registeredViaLabel(item.registeredVia)}</td>
                    <td className="warranty-nowrap">{dash(item.maskedPhone)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="warranty-more">
            {list.moreError && (
              <span className="warranty-field-msg is-error" role="alert">
                {list.moreError}
              </span>
            )}
            {list.nextCursor && (
              <Button className="Addbtn" onClick={list.loadMore} disabled={list.loadingMore}>
                {list.loadingMore ? "Loading" : "Load more"}
              </Button>
            )}
          </div>
        </>
      )}
    </WarrantyPage>
  );
};

export default AwaitingInvoices;
